import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AngularFireAuthGuard, redirectUnauthorizedTo, redirectLoggedInTo } from '@angular/fire/compat/auth-guard';
import { AccessLevel } from './services/permissions.service';
import { SubscriptionGuard } from './guards/subscription.guard';
import { GaurdsGuard } from './guards/auth.guard';

const redirectUnauthorizedToHome = () => redirectUnauthorizedTo(['/login']);


const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard'
  },
  {
    path: 'dashboard',
    canActivate: [GaurdsGuard, SubscriptionGuard],
    data: {
      menu: { show: true, label: 'Dashboard', icon: 'dashboard', order: 0 },
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    loadComponent: () => import('./pages/dashboard/dashboard.component').then((x) => x.DashboardComponent),
  },
  {
    path: 'login',
    pathMatch: 'full',
    loadChildren: () => import('./pages/login/login.module').then((x) => x.LoginModule)
  },
  {
    path: 'welcome',
    loadComponent: () => import('./pages/welcome/welcome.component').then((x) => x.WelcomeComponent),
  },
  {
    // Short card links: /s/{tenantToken}/{cardCode} — resolves and redirects
    // to the full board URL. No permissions data: any authenticated user may
    // attempt resolution; access is decided by what their claims can read.
    path: 's/:token/:code',
    canActivate: [GaurdsGuard],
    loadComponent: () => import('./pages/short-link/short-link.component').then((x) => x.ShortLinkComponent),
  },
  {
    path: 'boards',
    canActivate: [GaurdsGuard, SubscriptionGuard],
    data: {
      menu: { show: true, childPath:'boards/list', label: 'Boards', icon: 'view_column', order: 2 },
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READWRITE
      }
    },
    loadChildren: () => import('./pages/boards/board.module').then((x) => x.BoardPageModule),
  },
  {
    path: 'board',
    canActivate: [GaurdsGuard, SubscriptionGuard],
    data: {
      menu: { show: false, label: 'Board', icon: 'dashboard', order: 3 },
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READWRITE
      }
    },
    loadChildren: () => import('./pages/board/board.module').then((x) => x.BoardModule),
  },
  {
    path: 'project',
    canActivate: [GaurdsGuard, SubscriptionGuard],
    data: {
      menu: { show: true, label: 'Projects', icon: 'workspaces', order: 1 },
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    loadChildren: () => import('./pages/billableItems/billableItems.module').then((x) => x.BillableItemsPageModule),
  },

  {
    path: 'accept-invite',
    // No auth guard - users must be able to access this BEFORE logging in
    pathMatch: 'full',
    loadComponent: () => import('./pages/accept-invite/accept-invite.component').then((x) => x.AcceptInviteComponent),
  },
  {
    path: 'connect',
    // MCP OAuth handshake page. No auth guard — it handles its own sign-in redirect.
    pathMatch: 'full',
    loadComponent: () => import('./pages/connect/connect.component').then((x) => x.ConnectComponent),
  },
  {
    path: 'files',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Files', icon: 'folder', order: 4 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    pathMatch: 'full',
    loadChildren: () => import('./pages/file-explorer/file-explorer.module').then((x) => x.FileExplorerModule),
  },
  {
    path: 'users',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Users', icon: 'people', order: 5 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.BLOCKED
      }
    },
    loadComponent: () => import('./pages/users/users.component').then((m) => m.UsersComponent),
  },
  {
    path: 'timesheet',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Timesheet', icon: 'access_time', order: 6 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READWRITE
      }
    },
    loadChildren: () => import('./pages/timesheet/timesheet.module').then((x) => x.TimesheetPageModule),
  },
  {
    path: 'clients',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Clients', icon: 'business', order: 7 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    loadChildren: () => import('./pages/clients/clients.module').then((x) => x.ClientsModule),
  },
  {
    path: 'reports-dashboard',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Reports Dashboard', icon: 'assessment', order: 8 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    loadChildren: () => import('./pages/report-dashboard/report-dashboard.module').then((x) => x.ReportDashboardModule),
  },
  {
    path: 'oauth-callback',
    pathMatch: 'full',
    loadComponent: () => import('./pages/oauth-callback/oauth-callback').then((x) => x.OauthCallbackComponent),
  },
  {
    path: 'test',
    pathMatch: 'full',
    loadComponent: () => import('./pages/test/test.component').then((x) => x.TestComponent),
  },
  {
    path: 'leaderboard',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Leaderboard', icon: 'leaderboard', order: 8 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.READ
      }
    },
    loadComponent: () => import('./pages/leaderboard/leaderboard.component').then(m => m.LeaderboardComponent),
  },
  {
    path: 'admin/timesheet-delete',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Delete Timesheets', icon: 'delete', order: 9 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.BLOCKED
      }
    },
    loadComponent: () => import('./pages/admin/timesheet-delete/timesheet-delete.component').then((x) => x.TimesheetDeleteComponent),
  },
  {
    path: 'integrations',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, SubscriptionGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Integrations', icon: 'extension', order: 11 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.READWRITE,
        'Access': AccessLevel.BLOCKED
      }
    },
    loadComponent: () => import('./pages/integrations/integrations.component').then((x) => x.IntegrationsComponent),
  },
  {
    path: 'billing',
    pathMatch: 'full',
    canActivate: [GaurdsGuard, AngularFireAuthGuard],
    data: {
      menu: { show: true, label: 'Billing', icon: 'payment', order: 10 },
      authGuardPipe: redirectUnauthorizedToHome,
      permissions: {
        'Super-Admin': AccessLevel.READWRITE,
        'Admin': AccessLevel.BLOCKED,
        'Access': AccessLevel.BLOCKED
      }
    },
    loadComponent: () => import('./pages/billing/billing.component').then((x) => x.BillingComponent),
  },
  // Wildcard route - must be last
  {
    path: '**',
    redirectTo: 'dashboard'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
